// @ts-ignore
import { SYSTEM_ERROR } from '@/common/const'
import Fuse from 'fuse.js'
import { SenderAddressType, RecipientAddressType } from '@/types'
import range from 'lodash/range'
import concat from 'lodash/concat'

export const typefaces: TypefaceType[] = [
  {
    name: 'ゴシック体',
    icon: 'gothic.svg'
  },
  {
    name: '丸ゴシック体',
    icon: 'roundGothic.svg'
  },
  {
    name: '明朝体',
    icon: 'mincho.svg'
  },
  {
    name: '楷書体',
    icon: 'calligraphy.svg'
  }
]

export interface TypefaceType {
  name: string
  icon: string
}

export const fuseOptions: Fuse.IFuseOptions<SenderAddressType | RecipientAddressType> = {
  shouldSort: false,
  keys: [
    'zipCode',
    'fullAddress',
    'companyName',
    'department',
    'tel',
    'personalName',
    'personalNameRuby',
    'fullAddress',
    'address1',
    'address2',
    'area',
    'zipcode',
    'prefecture',
    'firstName',
    'firstNameKana',
    'lastName',
    'lastNameKana'
  ]
}

export const ENDPOINTS = {
  INITIAL_DATA: '/checkouts/initial_data',
  CART_ITEMS: '/cart_items',
  SENDER_ADDRESSES: '/sender_addresses',
  RECIPIENT_ADDRESSES: '/recipient_addresses',
  ZIPCODE: '/zip_codes/%zipcode',
  NEW_RECIPIENT_ADDRESSES: '/recipient_addresses',
  NEW_SENDER_ADDRESSES: '/sender_addresses',
  INVOICE: '/cart_items/invoice',
  CREDIT_CARD: '/credit_cards',
  COUPONS: '/coupons/%couponCode',
  ORDERS: '/orders',
  DELIVERY_DATE: '/printable_skus/%id/delivery_date'
} as const

export const addressType = {
  sender: 'sender',
  recipient: 'recipient'
}

export const paymentMethods = {
  creditcard: 'creditcard',
  npCredit: 'np_credit',
  bankTransfer: 'bank_transfer',
  payableCorp: 'payable_corp',
  convenienceStore: 'convenience_store'
} as const

export const paymentMethodsLabel = {
  [paymentMethods.payableCorp]: 'ラクスル請求書払い',
  [paymentMethods.creditcard]: 'クレジットカード',
  [paymentMethods.npCredit]: 'NP請求書払い',
  [paymentMethods.bankTransfer]: '銀行振込',
  [paymentMethods.convenienceStore]: 'コンビニ支払い'
} as const

export const ERROR_MESSAGES = {
  INVALID_CREDIT_CARD: 'クレジットカードが正しくありません。再確認ください。',
  ZIPCODE: '郵便番号が正しいか確認してください。',
  EMPTY_RECIPIENT_ADDRESS: 'お届け先を選択してください。',
  ADDRESS_NOT_FOUND: '住所が見つかりませんでした。',
  CREATE_CREDIT_CARD_FAIL:
    'クレジットカードの登録で問題が発生しました。時間をおいて再度お試しください。',
  SYSTEM_ERROR: SYSTEM_ERROR.UNKNOWN
} as const

export const rootErrorMessages: {
  [key: string]: {
    [code: string]: string
  }
} = {
  payment: {
    1: 'お支払い情報が正しくありません。',
    2: 'お支払い方法を選択してください'
  },
  comment: {
    1: 'ご希望のサイズが入力されていません。',
    2: '「会社名/会社の業界/会社・サービスサイトURL/利用用途」を全て入力してください。'
  },
  noshiComment: {
    1: '印字する内容を入力してください。'
  },
  noshiTypeface: {
    1: '書体を入力してください。'
  },
  quantity: {
    1: '数量の合計が正しく無い商品があります。',
    2: '注文できない単位で入力されている商品があります。',
    3: '数量が未入力の商品があります'
  },
  delivery: {
    1: 'お届け先が指定されていない商品があります。'
  },
  sizeQuantity: {
    1: 'ご希望のサイズまたは数量をご確認ください。'
  }
}

export const ORDER_API_ERROR_MESSAGES = {
  CO101: 'お届け先の郵便番号に誤りがあります。ご確認ください。', // Zip_code not found
  CO102CreditCard: '決済処理でエラーが発生しました。クレジットカードの番号と有効期限をご確認の上、再度ご注文ください。', // Payment not done
  CO102NpCredit: '決済処理でエラーが発生しました。請求先情報をご確認の上、再度ご注文ください。', // Payment not done
  CO103: '注文情報を最新化するため、ページを再読み込みしてください。恐れ入りますが、カートの内容を再度ご確認ください。', // Not update checkout page a long time
  CO104: 'サービスは不完全な要求を受け取りました。', // Bad request
  CO105: 'クオカードなどの金券類は、審査完了後にお買い求めいただけます。', // Cannot checkout with money ticket products and raksul payment not ready
  CO106: '請求先の郵便番号と住所が一致しません。現在の入力内容が正しい場合は<a href="/contact" target="_blank"> こちら </a>よりお問い合わせをお願いいたします。', // Invalid np customer address
  CO107: `ご利用可能額を超えています。現在増枠審査を行っており、半日から1営業日を目安に審査が完了します。<br>
          誠に恐れ入りますが少々お待ちいただけますと幸いです。<br>
          また、他決済方法をご利用いただくことも可能です。`,
  CO108: '氏名に機種依存文字や半角カナ等の利用できない文字列が含まれています。登録している氏名を変更するか他のお支払い方法をご利用ください。',
  CO109: '現在お客様のアカウントでは新規のご注文ができない状態です。支払期限を過ぎたご請求の未入金がある場合、弊社サービス利用規約に違反が認められた場合などのケースで新規ご注文の受付停止をさせていただくことがあります。', // Raksul credit is suspended
  CO110: 'デザインされた印刷色をなくなりました。別の印刷色を選択してください。', // Invalid printing color
  CO111: 'エラーが発生したため処理を中断しました。お手数ですが再度お試しください',
  CO112: 'エラーが発生したため処理を中断しました。お手数ですが再度お試しください'
} as const

export const UNREGISTERED_CART_ID = 'new'

export const COMMENT_TYPE = {
  CLOTHING: 'CLOTHING',
  VISA: 'VISA'
} as const

export type CommentType = keyof typeof COMMENT_TYPE

// Visa products requires comment section and only use bank transfer payment method
export const VISA_PRODUCTS_ID: number[] = concat([874, 875], range(3421, 3429), range(3435, 3531), range(3687, 3700), range(3831, 3843))

// Products require comment section and use all payment methods
export const REQUIRED_COMMENT_PRODUCTS_ID: number[] = range(3268, 3288)

export const commentDisplayTextVisa = {
  title: '審査用項目',
  tooltip: '審査に必要な項目です。この商品は法人様向けですので、全項目入力してください。',
  placeholder: '',
  errorMsg: '「会社名/会社の業界/会社・サービスサイトURL/利用用途」を全て入力してください。'
}

export const commentDisplayTextClothing = {
  title: 'ご希望サイズ',
  tooltip: 'サイズごとの必要枚数を記載してください',
  tooltipNote: '(サイズ一覧は商品ページに掲載)',
  placeholder: '記入例(合計10枚の場合)：Sサイズ3枚、Mサイズ6枚、XXLサイズ1枚',
  errorMsg: 'ご希望サイズごとの必要枚数を入力してください。'
}

export const userType = {
  BUSINESS: 'business'
}
